import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Subscription {
  readonly subscriptionId: string;
  readonly targetType: string;
  readonly targetRef: string;
  readonly isActive: boolean;
}

/** M27 — Subscription Management (§31). Lists the user's subscriptions and
 * supports unsubscribe (§18). */
export function M27SubscriptionManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Subscription[]>([]);
  const [total, setTotal] = useState(0);
  const [targetType, setTargetType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = targetType ? `?targetType=${encodeURIComponent(targetType)}` : '';
    apiRequest<{ data: Subscription[]; total: number }>(`/v1/collab/subscriptions${q}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, targetType]);

  async function unsubscribe(id: string): Promise<void> {
    try {
      await apiRequest(`/v1/collab/subscriptions/${id}`, { token, method: 'DELETE' });
      setRows((r) => r.filter((s) => s.subscriptionId !== id));
      setTotal((t) => t - 1);
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <main aria-label="subscription-management">
      <h1>{tr('m27.subscription.heading')}</h1>
      <label htmlFor="sub-type">{tr('m27.subscription.fldTargetType')}</label>
      <select id="sub-type" value={targetType} onChange={(e) => setTargetType(e.target.value)}>
        {['', 'TOPIC', 'PROJECT', 'DEPARTMENT', 'CAMPAIGN', 'CATEGORY', 'ANNOUNCEMENT', 'REGION'].map((t) => <option key={t || 'all'} value={t}>{t || tr('m27.subscription.optAll')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m27.subscription.loading')}</p> : (
        <>
          <p aria-label="subscription-count">{tr('m27.subscription.count', { count: total })}</p>
          <table aria-label="subscription-table">
            <thead><tr><th>{tr('m27.subscription.colTarget')}</th><th>{tr('m27.subscription.colReference')}</th><th>{tr('m27.subscription.colStatus')}</th><th>{tr('m27.subscription.colAction')}</th></tr></thead>
            <tbody>{rows.map((s) => (
              <tr key={s.subscriptionId}><td>{s.targetType}</td><td>{s.targetRef}</td><td>{s.isActive ? tr('m27.subscription.statusActive') : tr('m27.subscription.statusInactive')}</td>
                <td><button type="button" onClick={() => void unsubscribe(s.subscriptionId)}>{tr('m27.subscription.btnUnsubscribe')}</button></td></tr>
            ))}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
